import { toast } from 'sonner'

type SSOMode = 'signin' | 'signup'

/**
 * What the caller should do next. `missing-email` lets the form flag its own
 * email field rather than this helper firing a toast the user cannot act on.
 */
export type EnterpriseSSOOutcome = 'redirecting' | 'complete' | 'missing-email' | 'failed'

interface EnterpriseSSOParams {
  mode: SSOMode
  /**
   * The user's work email. Required: Clerk resolves the enterprise connection
   * from the email domain before redirecting, so there is no way to start the
   * flow without it.
   */
  emailAddress?: string
  signIn?: ReturnType<typeof import('@clerk/clerk-react').useSignIn>['signIn']
  signUp?: ReturnType<typeof import('@clerk/clerk-react').useSignUp>['signUp']
  isLoaded: boolean
  /** Path the IdP returns to; the callback is completed there. */
  redirectPath?: string
}

/**
 * Clerk models SAML, OIDC and EASIE connections as one "enterprise connection"
 * behind a single strategy - the protocol is dashboard configuration, not a
 * client concern. Every one of them is email-domain-first: Clerk matches the
 * domain to the connection *before* redirecting to the IdP.
 */
export const ENTERPRISE_SSO_STRATEGY = 'enterprise_sso' as const

/**
 * True for a sign-in whose first factor went through an enterprise connection.
 *
 * The *verification* reports the underlying provider rather than the strategy we
 * asked for: an OIDC connection comes back as `oauth_custom_<slug>`, an EASIE one
 * as `oauth_microsoft`/`oauth_google`. Only `supported_first_factors` says
 * `enterprise_sso`, so matching on that alone would miss every real callback.
 */
export const isEnterpriseSSOStrategy = (strategy?: string | null): boolean =>
  strategy === ENTERPRISE_SSO_STRATEGY || Boolean(strategy?.startsWith('oauth_'))

/**
 * `transferable` means the IdP authenticated the user but Clerk holds no account
 * for them yet, so the sign-in has to be handed over to a sign-up.
 */
export const isTransferableSSO = (verification?: { status: string | null } | null): boolean =>
  verification?.status === 'transferable'

/**
 * Convert a transferable SSO sign-in into a sign-up, provisioning the Clerk user
 * just in time. Returns the new session id, or null when the account could not
 * be created (the caller stays on the sign-in form).
 */
export const transferSSOToSignUp = async (
  signUp?: ReturnType<typeof import('@clerk/clerk-react').useSignUp>['signUp']
): Promise<string | null> => {
  try {
    const result = await signUp?.create({ transfer: true })

    if (result?.status === 'complete' && result.createdSessionId) return result.createdSessionId

    // Clerk needs fields the IdP did not supply (commonly first/last name).
    toast.error('Could not finish creating your account', {
      description: 'Your identity provider did not return everything we need. Please contact your administrator.',
    })
    return null
  } catch (err) {
    const clerkError = (err as { errors?: { code?: string; longMessage?: string }[] })?.errors?.[0]

    // Just-in-time provisioning is off, so SSO can only sign in users who already
    // exist in Clerk.
    if (clerkError?.code === 'enterprise_sso_jit_provisioning_disabled') {
      toast.error('No account found', {
        description:
          'Single sign-on succeeded but you do not have an account yet. Please ask your administrator to invite you.',
      })
      return null
    }

    toast.error('Single sign-on failed', {
      description: clerkError?.longMessage ?? 'Could not create your account. Please contact your administrator.',
    })
    return null
  }
}

/** The shape both SignIn and SignUp verifications share for the redirect handoff. */
interface PendingVerification {
  status: string | null
  externalVerificationRedirectURL: URL | null
}

const getErrorDescription = (err: unknown, mode: SSOMode): string => {
  const clerkError = (err as { errors?: { code?: string; longMessage?: string; message?: string }[] })?.errors?.[0]
  const action = mode === 'signin' ? 'sign in' : 'sign up'

  // No enterprise connection serves this email domain - the most common failure,
  // and the one the user can actually act on.
  if (clerkError?.code === 'enterprise_sso_connection_not_found' || clerkError?.code === 'identifier_not_found') {
    return `Single sign-on is not set up for this email domain. Please use your password to ${action}, or contact your administrator.`
  }

  const message = clerkError?.longMessage ?? clerkError?.message ?? (err instanceof Error ? err.message : undefined)

  return message ? `${message} Please use another method to ${action}.` : `Please use another method to ${action}.`
}

export const handleEnterpriseSSO = async ({
  mode,
  emailAddress,
  signIn,
  signUp,
  isLoaded,
  redirectPath = '/sign-in',
}: EnterpriseSSOParams): Promise<EnterpriseSSOOutcome> => {
  if (!isLoaded) return 'failed'

  const identifier = emailAddress?.trim()
  if (!identifier) return 'missing-email'

  try {
    const redirectUrl = window.location.origin + redirectPath

    // `oidcPrompt` is passed through to the IdP's authorize call so the user is
    // offered an account chooser instead of being signed in silently as whoever
    // the browser already holds a session for.
    const common = { redirectUrl, actionCompleteRedirectUrl: redirectUrl, oidcPrompt: 'select_account' }

    const result =
      mode === 'signin'
        ? await signIn?.create({ ...common, strategy: ENTERPRISE_SSO_STRATEGY, identifier })
        : await signUp?.create({ ...common, strategy: ENTERPRISE_SSO_STRATEGY, emailAddress: identifier })

    if (result?.status === 'complete') {
      toast.success('Welcome to CISO Adapt!', {
        description: `You are now signed ${mode === 'signin' ? 'in' : 'up'} to your account`,
      })
      return 'complete'
    }

    // Both resources carry the IdP redirect on their pending verification, but
    // under different keys.
    const verification: PendingVerification | undefined | null =
      result && 'firstFactorVerification' in result
        ? result.firstFactorVerification
        : result?.verifications?.externalAccount

    if (verification?.externalVerificationRedirectURL) {
      window.location.href = verification.externalVerificationRedirectURL.toString()
      return 'redirecting'
    }

    // Already verified but not finalised - the callback handler in the sign-in
    // component completes the flow.
    if (verification?.status === 'verified') return 'complete'

    throw new Error('Single sign-on did not complete')
  } catch (err) {
    toast.error('Single sign-on failed', {
      description: getErrorDescription(err, mode),
    })
    return 'failed'
  }
}
